// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import styles from './styles';

export default function PopupWindow({
  x,
  y,
  attributes,
  index,
  totalCount,
  onPrev,
  onNext,
  onClose,
  parentRef,
}) {
  const s = styles('', {}, 0, false, false, false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: x, top: y, transform: '' });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Clamp popup inside parent bounds when x/y changes
  useEffect(() => {
    if (!popupRef.current) return;
    const parent = popupRef.current.offsetParent;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const clampedX = Math.max(0, Math.min(x, parentRect.width - popupRect.width));
    const clampedY = Math.max(0, Math.min(y, parentRect.height - popupRect.height));
    dragging.current = false;
    setPosition({ left: clampedX, top: clampedY, transform: 'translate(0,0)' });
  }, [x, y]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!popupRef.current) return;
    dragging.current = true;
    const popupRect = popupRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - popupRect.left,
      y: e.clientY - popupRect.top,
    };
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || !popupRef.current) return;
    const parentRect = popupRef.current.offsetParent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    let newX = e.clientX - parentRect.left - dragOffset.current.x;
    let newY = e.clientY - parentRect.top - dragOffset.current.y;
    newX = Math.max(0, Math.min(newX, parentRect.width - popupRect.width));
    newY = Math.max(0, Math.min(newY, parentRect.height - popupRect.height));
    setPosition({ left: newX, top: newY, transform: 'translate(0,0)' });
  };

  useEffect(() => {
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const INTERNAL_KEYS = new Set(['geometry', 'layerName', 'layerColor', 'Layer Name']);

  // The "Pole Image" field carries one or more newline-separated S3 image URLs.
  // Render each as a thumbnail; clicking one opens the full image in a new tab.
  // A broken image degrades to alt text.
  const renderValue = (key: string) => {
    const text = String(attributes[key] ?? '');
    if (key === 'Pole Image' && text.trim()) {
      const urls = text.split('\n').map((url) => url.trim()).filter(Boolean);
      return (
        <span style={s.popupImageGroup}>
          {urls.map((url) => {
            const href = encodeURI(url);
            return (
              <a key={url} href={href} target="_blank" rel="noopener noreferrer">
                <img src={href} alt="Pole" style={s.popupImage} />
              </a>
            );
          })}
        </span>
      );
    }
    return text;
  };

  const dynamicRows = Object.keys(attributes)
    .filter((key) => !INTERNAL_KEYS.has(key))
    .map((key, i) => (
      <tr key={key} style={s.popupTableRow(i)}>
        <td style={s.popupTableCellKey}>{key}</td>
        <td style={s.popupTableCellValue}>{renderValue(key)}</td>
      </tr>
    ));

  // If no fields exist, show only Layer Name row, otherwise show only the fields
  const rows = dynamicRows.length > 0 ? dynamicRows : [
    <tr key="__layerName__" style={s.popupTableRow(0)}>
      <td style={s.popupTableCellKey}>Layer Name</td>
      <td style={s.popupTableCellValue}>{attributes.layerName || 'Feature'}</td>
    </tr>,
  ];

  return (
    <div ref={popupRef} style={s.popupContainer(position)}>
      <div style={{ ...s.popupHeader }} onMouseDown={handleMouseDown}>
        <div style={s.popupLayerInfo}>
          <div style={s.layerColorBox(attributes.layerColor)}></div>
          <span>{attributes.layerName || 'Feature'}</span>
        </div>
        <div style={s.popupControls}>
          <button disabled={index === 0} onClick={onPrev} style={s.prev_btn}>‹</button>
          <span>{index + 1} of {totalCount}</span>
          <button disabled={index === totalCount - 1} onClick={onNext} style={s.next_btn}>›</button>
          <span style={s.popupClose} onClick={onClose}>×</span>
        </div>
      </div>
      <div style={s.popupContentWrapper}>
        <table style={s.popupTable}>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}